import {
  personTileUrl,
  personTileFallbackUrl,
  groundTileUrl,
  stateToStage,
} from "../tiles.js";
import {
  clearGuestData,
  clearGuestSession,
  enableGuest,
  guestApi,
  isGuest,
} from "./guest-store.js";

const TOKEN_KEY = "growme_token";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setToken(token) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      clearGuestSession();
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function clearToken() {
  setToken("");
}

export function startGuest() {
  clearToken();
  enableGuest();
}

export function endGuest() {
  clearGuestData();
}

export { isGuest };

async function request(path, options = {}) {
  const headers = {
    "content-type": "application/json",
    ...(options.headers || {}),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(path, {
    ...options,
    headers,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = data.error;
    throw err;
  }
  return data;
}

function remoteApi() {
  return {
    garden: () => request("/api/garden"),
    people: (q = "") => request(`/api/people${q ? `?q=${encodeURIComponent(q)}` : ""}`),
    person: (id) => request(`/api/people/${id}`),
    createPerson: (body) => request("/api/people", { method: "POST", body: JSON.stringify(body) }),
    updatePerson: (id, body) =>
      request(`/api/people/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    deletePerson: (id) => request(`/api/people/${id}`, { method: "DELETE" }),
    events: (params = {}) => {
      const qs = new URLSearchParams();
      if (params.personId) qs.set("personId", params.personId);
      if (params.place) qs.set("place", params.place);
      const suffix = qs.toString() ? `?${qs}` : "";
      return request(`/api/events${suffix}`);
    },
    createEvent: (body) => request("/api/events", { method: "POST", body: JSON.stringify(body) }),
    updateEvent: (id, body) =>
      request(`/api/events/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    deleteEvent: (id) => request(`/api/events/${id}`, { method: "DELETE" }),
    places: () => request("/api/places"),
    suggestPlaces: ({ q = "", personIds = [] } = {}) => {
      const qs = new URLSearchParams({ suggest: "1" });
      if (q) qs.set("q", q);
      if (personIds.length) qs.set("personIds", personIds.join(","));
      return request(`/api/places?${qs}`);
    },

    me: () => request("/api/auth/me"),
    login: (body) => request("/api/auth/login", { method: "POST", body: JSON.stringify(body) }),
    signup: (body) => request("/api/auth/signup", { method: "POST", body: JSON.stringify(body) }),
    logout: () => request("/api/auth/logout", { method: "POST", body: "{}" }),
    forgotPassword: (body) =>
      request("/api/auth/forgot-password", { method: "POST", body: JSON.stringify(body) }),
    resetPassword: (body) =>
      request("/api/auth/reset-password", { method: "POST", body: JSON.stringify(body) }),
    resendVerification: (body) =>
      request("/api/auth/resend-verification", { method: "POST", body: JSON.stringify(body) }),
  };
}

/** API surface — guest mode uses localStorage instead of the server. */
export const api = new Proxy(
  {},
  {
    get(_target, prop) {
      if (typeof prop !== "string") return undefined;
      if (isGuest() && prop in guestApi) {
        return guestApi[prop];
      }
      const remote = remoteApi();
      if (prop in remote) return remote[prop];
      return undefined;
    },
  },
);

/** @deprecated use personTileUrl — kept for empty-states */
export function tileArtUrl(state) {
  return personTileFallbackUrl(state);
}

export function tileFallbackUrl(state) {
  return personTileFallbackUrl(state);
}

export { personTileUrl, personTileFallbackUrl, groundTileUrl, stateToStage };
